import { useState, useEffect } from 'react';
import { SentimentIcon } from "./SentimentIcon";
import { Formatter } from "../format";
import Tag from "./Tag";


const getTranscriptDetails = (allSegments) => {
  let offsetStartValue = 0;
  let offsetEndValue = 0;
  return allSegments.reduce((accumulator, item) => {
    offsetStartValue = offsetEndValue === 0 ? offsetEndValue : offsetEndValue + 1;
    offsetEndValue = offsetStartValue + item?.Text.trim().length;
    
    accumulator =[...accumulator,{content: item.Text.trim(), start: item.StartTime, end: item.EndTime, offsetStart: offsetStartValue, offsetEnd: offsetEndValue}];
    return accumulator;
  },[]);
}

const generateTranscriptElement = (text, allSegments, highlightLocations) => {
  if (!allSegments.length) {
    return applyReplacements(text, highlightLocations);
  }
  const transcript = getTranscriptDetails(allSegments);
  
  return  transcript.map((segment, i) => {
    const addHighlight = highlightLocations.filter(highlight => highlight.start === segment.offsetStart || highlight.end === segment.offsetEnd || (highlight.start <= segment.offsetStart && highlight.end >= segment.offsetEnd) || (highlight.end >= segment.offsetStart && highlight.end <= segment.offsetEnd));
    const content = segment.content + " " ;
    const key = "segment-"+ segment.offsetStart;

    if(addHighlight.length) {
      return addHighlight[0].fn(content, key, segment.start, segment.end, segment.offsetStart, segment.offsetEnd);
    }
    return <span data-start={segment.start} data-end={segment.end} data-offset-start={segment.offsetStart} data-offset-end={segment.offsetEnd} key={key}>{content}</span>
  });
}

export const TranscriptSegment = ({
  name,
  allSegments,
  segmentStart,
  text,
  translatedText,
  globalEnglish,
  onClick,
  highlightLocations,
  score,
  interruption,
  ivr,
  categoryList,
}) => {
  const [showEnglish, setShowEnglish] = useState(false);
  useEffect(() => { setShowEnglish(globalEnglish); }, [globalEnglish]);
  const displayText = showEnglish ? (translatedText || text) : text;
  const displaySegs = showEnglish ? [] : allSegments;
  const displayHighlight = showEnglish ? [] : highlightLocations;

  // Chat-style layout: the agent's turns sit on the right in a tinted bubble,
  // the customer's on the left, so the shape of the conversation reads at a
  // glance without parsing every speaker label.
  const isAgent = /agent/i.test(name || '');
  const tags = [
    ivr && <Tag key="ivr" bg="danger">IVR</Tag>,
    interruption && <Tag key="interruption" bg="warning" text="dark">Interruption</Tag>,
    ...categoryList.map((category, j) => <Tag key={`cat-${j}`} bg="primary">{category}</Tag>),
  ].filter(Boolean);

  return (
    <div className={`flex gap-2.5 ${isAgent ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className="flex-shrink-0 mt-5">
        <SentimentIcon score={score} size="1.4em" />
      </div>

      <div className={`min-w-0 max-w-[78%] ${isAgent ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`flex items-center gap-1.5 mb-1 text-xs ${isAgent ? 'flex-row-reverse' : ''}`}>
          <span className={`font-semibold ${isAgent ? 'text-cloudza-700' : 'text-gray-700'}`}>{name}</span>
          <span className="text-gray-300">·</span>
          <span
            className="audio-start text-gray-400 hover:text-cloudza-600 cursor-pointer tabular-nums"
            data-currenttime={segmentStart}
            onClick={onClick}
          >
            {Formatter.Time(segmentStart)}
          </span>
          {translatedText && (
            <button
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              onClick={() => setShowEnglish(s => !s)}
            >
              {showEnglish ? 'ZU' : 'EN'}
            </button>
          )}
        </div>

        <div
          className={`px-3.5 py-2.5 text-sm leading-relaxed ${
            isAgent
              ? 'bg-cloudza-50 text-gray-800 rounded-2xl rounded-tr-sm'
              : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-tl-sm'
          }`}
        >
          {generateTranscriptElement(displayText, displaySegs, displayHighlight)}
        </div>

        {tags.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1.5 ${isAgent ? 'justify-end' : ''}`}>{tags}</div>
        )}
      </div>
    </div>
  );
};

// substituteAt replaces a subset of a string with the value of the fn provided.
// it returns an array containing string and react elements
const substituteAt = (input, beginOffset, endOffset, fn, key) => {
  let before = input.slice(0, beginOffset);
  let after = input.slice(endOffset);

  const target = input.slice(beginOffset, endOffset);
  const replaced = fn(target, key);
  return [before, replaced, after];
};

const wrapper = (input, ...opts) => {
  if (!Array.isArray(input)) input = [input];

  return input
    .map((chunk, i) =>
      typeof chunk === "string" && i === 0
        ? substituteAt(chunk, ...opts)
        : chunk
    )
    .flat();
};

// applyReplacements applies a series of string replacements to the input.
// each replacement should consist of a start offset, end offset and
// function that transforms the target string
// Substrings identified by start and end offsets cannot overlap
export const applyReplacements = (input, replacements) =>
  replacements
    .sort(sortFn)
    .reduceRight(
      (accumulator, { start, end, fn }, i) =>
        wrapper(accumulator, start, end, fn, i),
      input
    );

const sortFn = (a, b) => {
  if (a.start > b.start) return 1;
  if (a.start < b.start) return -1;
  return 0;
};
